import React from "react";
import PlaceholderImage from "../../assets/img/common/nft-project-img.png";
import { Link, useNavigate } from "react-router-dom";
import ReactTimeAgo from "react-time-ago";
import useNavigateToProfile from "../../hooks/useNavigateToProfile";
import UserPlaceHolderImg from "../../assets/img/common/user-placeholder-img.png";
import { ReactComponent as EditIcon } from "../../assets/img/common/profile-edit-icon.svg";
import { ReactComponent as TrashIcon } from "../../assets/img/common/trash-red-icon.svg";
import {
  useDislikeResultMutation,
  useLazyGetResultsQuery,
  useLikeResultMutation,
  useRemoveResultMutation,
} from "../../redux/slices/api/resultApiSlice";
import { handleError } from "../../hooks/useApi";
import { useLazyGetProjectByIdQuery } from "../../redux/slices/api/projectsApiSlice";
import notify from "../common/notify";
import useCheckProjectPermissions from "../../hooks/useCheckProjectPermissions";
import LikeButton from "../common/LikeButton";
import { FaCrown, FaRegComment } from "react-icons/fa";
const ResultCard = ({
  resultData,
  projectData,
  containerClass = "w-full",
  isOnDiscovery = false,
}) => {
  const {
    feature_image,
    _id: resultId,
    title,
    publishedAt,
    createdAt,
    writers,
    result_summary,
    num_id,
    likes,
    comments_count,
    premium,
    keywords,
  } = resultData ?? {};
  const { _id: projectId } = projectData;
  const { navigateToProfile } = useNavigateToProfile();
  const { hasAccessToResult } = useCheckProjectPermissions({ projectData });

  const [removeResult, { isLoading: removeResultLoading }] =
    useRemoveResultMutation();
  const [triggerGetProjectData] = useLazyGetProjectByIdQuery();
  const [triggerGetResultsData] = useLazyGetResultsQuery();
  const onToggleLike = async () => {
    try {
      if (isOnDiscovery) await triggerGetResultsData().unwrap();
      else await triggerGetProjectData({ id: projectId }).unwrap();
    } catch (error) {
      handleError(error, null, true);
    }
  };
  const handleRemoveResult = async (e) => {
    e.stopPropagation();
    try {
      const removeResponse = await removeResult({ id: resultId }).unwrap();
      await triggerGetProjectData({ id: projectId }).unwrap();
      notify(removeResponse?.message, "success");
    } catch (error) {
      handleError(error, null, true);
    }
  };
  const navigate = useNavigate();
  return (
    <button
      onClick={() => navigate(`/results/${resultId}`)}
      className={`${containerClass} flex items-center ${
        isOnDiscovery ? "min-h-[224px]" : "h-[200px]"
      } mlg:h-56 rounded-2xl shadow-md hover:shadow-xl bg-gradient-to-br from-white to-gray-50 px-4 mlg:px-6 py-4 relative result-card transition-all duration-300 hover:scale-[1.01] group`}
    >
      <div className="hidden mlg:block mlg:min-w-[200px] mlg:h-48 min-w-[64px] h-20 rounded-xl relative overflow-hidden group-hover:shadow-lg transition-shadow duration-300">
        {hasAccessToResult && (
          <div className="w-full flex justify-between items-center px-3 absolute left-0 top-3 z-10">
            <Link
              onClick={(e) => e.stopPropagation()}
              to={`/create-result?project-id=${projectId}&result-id=${resultId}`}
              className="w-8 h-8 p-1.5 flex justify-center items-center rounded-full bg-primary hover:bg-[#7e7cff] transition-all duration-300 shadow-lg hover:scale-110"
            >
              <EditIcon className="w-4 h-4" />
            </Link>
            <button
              disabled={removeResultLoading}
              className="w-8 h-8 flex justify-center items-center rounded-full bg-white hover:bg-red-50 transition-all duration-300 shadow-lg hover:scale-110"
              onClick={handleRemoveResult}
            >
              <TrashIcon className="w-5 h-5" />
            </button>
          </div>
        )}
        <img
          src={feature_image ?? PlaceholderImage}
          alt="feature_image"
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
      </div>
      {/* ////// result detail  */}
      <div className="mlg:max-w-[calc(100%-208px)] w-full flex flex-col mlg:pl-6 mlg:pt-3 pb-1 mlg:pb-4 h-full justify-between">
        <div className="w-full flex items-start">
          {/* -------- result image mobile ------- */}
          <div className="mlg:hidden flex flex-col relative overflow-hidden">
            <img
              src={feature_image ?? PlaceholderImage}
              alt="feature_image"
              className="min-w-[80px] max-w-[80px] h-[80px] object-cover rounded-xl shadow-sm"
            />
            {hasAccessToResult && (
              <div className="w-full flex justify-between items-center mt-1.5">
                <Link
                  onClick={(e) => e.stopPropagation()}
                  to={`/create-result?project-id=${projectId}&result-id=${resultId}`}
                  className="w-6 h-6 p-1 flex justify-center items-center rounded-full bg-primary hover:bg-[#7e7cff] transition-all duration-300 shadow-md"
                >
                  <EditIcon className="w-3 h-3" />
                </Link>
                <button
                  disabled={removeResultLoading}
                  className="w-6 h-6 flex justify-center items-center rounded-full bg-white hover:bg-red-50 transition-all duration-300 shadow-md"
                  onClick={handleRemoveResult}
                >
                  <TrashIcon className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>

          <div className="max-mlg:max-w-[calc(100%-90px)] w-full flex flex-col pl-3 mlg:pl-0">
            <section className="w-full flex flex-col">
              <div className="w-full flex flex-col mlg:flex-row mlg:justify-between mlg:items-start">
                <div className="font-bold text-lg mlg:text-xl text-left max-w-fit pr-3 truncate group-hover:text-primary transition-colors duration-300">
                  {title.slice(0, 60)}
                  {title?.length > 60 ? "..." : ""}
                </div>
                <div className="min-w-fit flex items-center mt-1 mlg:mt-0">
                  {premium && (
                    <FaCrown className="text-yellow-500 text-xl mr-2" />
                  )}
                  <ReactTimeAgo
                    date={publishedAt ?? createdAt}
                    locale="en-US"
                    className="text-sm text-gray-500 min-w-fit"
                    timeStyle=""
                  />{" "}
                </div>
              </div>
              {isOnDiscovery && (
                <div className="flex items-center flex-wrap gap-2 mt-3">
                  {keywords?.length
                    ? keywords?.slice(0, 3)?.map((item, index) => (
                        <Link
                          onClick={(e) => {
                            e.stopPropagation();
                          }}
                          to={`/projects?keywords=${item}`}
                          key={index}
                          className="py-1.5 px-3 rounded-full bg-gradient-to-r from-purple-50 to-blue-50 text-primary text-xs font-medium hover:from-purple-100 hover:to-blue-100 transition-all duration-300 border border-purple-100"
                        >
                          {item}
                        </Link>
                      ))
                    : null}
                </div>
              )}
            </section>

            <p
              className={`text-left text-gray-600 text-sm leading-relaxed result-summary max-mlg:mt-3 mlg:mt-2 ${
                isOnDiscovery
                  ? "max-h-[100px] lg:max-h-[60px]"
                  : "max-h-[120px] lg:max-h-[80px]"
              }`}
            >
              {result_summary}
              ...
            </p>
          </div>
        </div>
        <div className="w-full flex justify-between items-center max-mlg:mt-3 mlg:mt-2">
          <div className="flex items-center">
            <button
              onClick={(e) => {
                e.stopPropagation();
                navigateToProfile(writers[0]?._id, writers[0]?.role, false);
              }}
              className="rounded-full overflow-hidden flex justify-center items-center cursor-pointer h-10 w-10 ring-2 ring-gray-200 hover:ring-primary transition-all duration-300"
            >
              <img
                src={writers[0]?.images?.profile || UserPlaceHolderImg}
                alt={title}
                className="transition-all duration-300 hover:scale-110 w-full h-full object-cover"
              />
            </button>
            <span className="flex flex-col ml-3 justify-between items-start">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  navigateToProfile(writers[0]?._id, writers[0]?.role, false);
                }}
                className="text-sm font-bold hover:text-primary transition-colors duration-300"
              >
                {writers[0]?.fundername ??
                  `${writers[0]?.firstname} ${writers[0]?.lastname}`}
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  navigateToProfile(writers[0]?._id, writers[0]?.role);
                }}
                className="text-gray-500 text-xs truncate hover:text-gray-700 transition-colors duration-300"
              >
                {writers[0]?.title_rank ||
                  writers[0]?.location ||
                  writers[0]?.email}
              </button>
            </span>
          </div>
          <div className="flex items-start gap-x-5 -mb-2">
            {!isOnDiscovery && (
              <span className="font-bold text-gray-600">#{num_id}</span>
            )}
            <LikeButton
              likes={likes}
              useLikeMutation={useLikeResultMutation}
              useDislikeMutation={useDislikeResultMutation}
              id={resultId}
              onToggleLike={onToggleLike}
              containerClass="w-8 h-9"
              heartClass="w-14 h-14"
              countClass="text-xs -mt-4"
              likeType="result"
            />{" "}
            <div className="flex flex-col items-center mt-0.5 hover:text-primary transition-colors duration-300">
              <FaRegComment size={19} className="text-gray-600" />
              <span className="text-xs mt-1 font-medium">{comments_count || 0}</span>
            </div>
          </div>
        </div>
      </div>
    </button>
  );
};

export default ResultCard;
